/// <amd-module name="Artifacts/FeedSettings/redux/Actions" />
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { ViewPermissionMap } from "Artifacts/FeedSettings/redux/Contracts";
import { IFeedSettings, IUpstreamSource } from "Artifacts/FeedSettings/types";
import { IDetailsTabFormState } from "Artifacts/FeedSettings/types/IDetailsTabFormState";
import { ILoadFailedPayload } from "Artifacts/Root/redux";
import { IFeed } from "Artifacts/Root/types";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import { Feed, FeedPermission, FeedView } from "Artifacts/Root/web-apis/contracts/Feed";
import {
  ActionsUnion,
  createAction,
  trackedAction,
  trackedErrorAction,
} from "redux/Utils/ActionHelper";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

export const enum FeedSettingsActionTypes {
  InitLoad = "Artifacts/FeedSettings/InitLoad",
  InitLoadStarted = "Artifacts/FeedSettings/InitLoadStarted",
  InitLoadCompleted = "Artifacts/FeedSettings/InitLoadCompleted",
  InitLoadFailed = "Artifacts/FeedSettings/InitLoadFailed",

  // General Settings
  SaveGeneralFeedSettings = "Artifacts/FeedSettings/SaveGeneralSettings",
  SaveGeneralFeedSettingsCompleted = "Artifacts/FeedSettings/SaveGeneralSettingsCompleted",
  SaveGeneralFeedSettingsFailed = "Artifacts/FeedSettings/SaveGeneralSettingsFailed",
  DeleteFeed = "Artifacts/FeedSettings/DeleteFeed",
  DeleteFeedCompleted = "Artifacts/FeedSettings/DeleteFeedCompleted",
  DeleteFeedFailed = "Artifacts/FeedSettings/DeleteFeedFailed",

  // Deleted Feed actions
  PermanentDeleteFeed = "Artifacts/FeedSettings/PermanentDeleteFeed",
  PermanentDeleteFeedCompleted = "Artifacts/FeedSettings/PermanentDeleteFeedCompleted",
  PermanentDeleteFeedFailed = "Artifacts/FeedSettings/PermanentDeleteFeedFailed",
  RestoreFeed = "Artifacts/FeedSettings/RestoreFeed",
  RestoreFeedCompleted = "Artifacts/FeedSettings/RestoreFeedCompleted",
  RestoreFeedFailed = "Artifacts/FeedSettings/RestoreFeedFailed",

  // Permissions
  LoadPermissions = "Artifacts/FeedSettings/LoadPermissions",
  LoadPermissionsCompleted = "Artifacts/FeedSettings/LoadPermissionsCompleted",
  LoadPermissionsFailed = "Artifacts/FeedSettings/LoadPermissionsFailed",
  AddPermission = "Artifacts/FeedSettings/AddPermission",
  AddPermissionCompleted = "Artifacts/FeedSettings/AddPermissionCompleted",
  AddPermissionFailed = "Artifacts/FeedSettings/AddPermissionFailed",
  DeletePermission = "Artifacts/FeedSettings/DeletePermission",
  DeletePermissionCompleted = "Artifacts/FeedSettings/DeletePermissionCompleted",
  DeletePermissionFailed = "Artifacts/FeedSettings/DeletePermissionFailed",

  // Views
  LoadViewsPermissions = "Artifacts/FeedSettings/LoadViewsPermissions",
  LoadViewsPermissionsCompleted = "Artifacts/FeedSettings/LoadViewsPermissionsCompleted",
  LoadViewsPermissionsFailed = "Artifacts/FeedSettings/LoadViewsPermissionsFailed",
  AddOrEditView = "Artifacts/FeedSettings/AddOrEditView",
  AddOrEditViewCompleted = "Artifacts/FeedSettings/AddOrEditViewCompleted",
  AddOrEditViewFailed = "Artifacts/FeedSettings/AddOrEditViewFailed",
  MakeViewDefault = "Artifacts/FeedSettings/MakeViewDefault",
  MakeViewDefaultCompleted = "Artifacts/FeedSettings/MakeViewDefaultCompleted",
  MakeViewDefaultFailed = "Artifacts/FeedSettings/MakeViewDefaultFailed",
  DeleteView = "Artifacts/FeedSettings/DeleteView",
  DeleteViewCompleted = "Artifacts/FeedSettings/DeleteViewCompleted",
  DeleteViewFailed = "Artifacts/FeedSettings/DeleteViewFailed",

  // Upstreams
  UpdateUpstreamList = "Artifacts/FeedSettings/UpdateUpstreamList",
  AddUpstreams = "Artifacts/FeedSettings/AddUpstreams",

  LoadFeeds = "Artifacts/FeedSettings/LoadFeeds",
  LoadFeedsCompleted = "Artifacts/FeedSettings/LoadFeedsCompleted",
  LoadFeedsFailed = "Artifacts/FeedSettings/LoadFeedsFailed",

  LoadFeedViews = "Artifacts/FeedSettings/LoadFeedViews",
  LoadFeedViewsCompleted = "Artifacts/FeedSettings/LoadFeedViewsCompleted",
  LoadFeedViewsFailed = "Artifacts/FeedSettings/LoadFeedViewsFailed",

  SaveUpstreamConfiguration = "Artifacts/FeedSettings/SaveUpstreams",
  SaveUpstreamConfigurationCompleted = "Artifacts/FeedSettings/SaveUpstreamsCompleted",
  SaveUpstreamConfigurationFailed = "Artifacts/FeedSettings/SaveUpstreamsFailed",
  SaveUpstreamConfigurationComponentReset = "Artifacts/FeedSettings/SaveUpstreamResetComponentReset",

  LoadServiceConnections = "Artifacts/FeedSettings/LoadServiceConnections",
  LoadServiceConnectionsCompleted = "Artifacts/FeedSettings/LoadServiceConnectionsCompleted",
  LoadServiceConnectionsFailed = "Artifacts/FeedSettings/LoadServiceConnectionsFailed",
}

export interface ISettingsInitLoadPayload {
  feed: IFeed;
}

export interface IDeleteFeedPayload {
  feed: IFeed;
  onSuccess: () => void;
}

export interface IRestoreFeedPayload {
  feed: IFeed;
  onSuccess: () => void;
}

export interface ISaveFailedPayload {
  error: string;
}

export interface ILoadPermissionsCompletedPayload {
  permissions: FeedPermission[];
}

export interface ILoadViewsPermissionsCompletedPayload {
  permissionMap: Record<string, FeedPermission[]>;
}

export interface ILoadViewsPermissionsPayload {
  feed: IFeed;
  views: ViewIdentifier[];
}

export interface IAddOrEditViewPayload {
  feed: IFeed;
  view: FeedView;
  isEdit: boolean;
  isDefaultEnabled: boolean;
  currentViewPermissions: FeedPermission[];
  feedPermsToSet: FeedPermission[];
  onSuccess: () => void;
}

export interface IAddOrEditViewCompletedPayload {
  newModifiedView: FeedView;
  permissions: FeedPermission[];
  defaultFeedViewId?: string;
}

export interface IMakeViewDefaultPayload {
  feed: IFeed;
  view: FeedView;
}

export interface IMakeViewDefaultCompletedPayload {
  defaultViewId: string;
}

export interface IDeleteViewPayload {
  feed: IFeed;
  view: FeedView;
  onSuccess: () => void;
}

export interface IDeleteViewCompletedPayload {
  feed: IFeed;
  view: FeedView;
}

export interface ISettingsInitLoadCompletedPayload {
  settings: IFeedSettings;
}

export interface ISaveUpstreamsPayload {
  feed: IFeed;
  upstreams: IUpstreamSource[];
  onSuccess: () => void;
}

export interface IUpstreamsPayload {
  upstreams: IUpstreamSource[];
}

export interface IAddUpstreamsPayload {
  upstreams: IUpstreamSource[];
}

export interface IUpstreamPayload {
  upstream: IUpstreamSource;
}

export interface ILoadUpstreamFeedsCompleted {
  feeds: Feed[];
}

export interface ILoadFeedViewsPayload {
  feed: Feed;
}

export interface ILoadFeedViewsCompletedPayload {
  views: FeedView[];
}

export interface ILoadServiceConnectionsCompleted {
  serviceConnections: ServiceEndpoint[];
}

export interface ISaveGeneralFeedSettingsPayload {
  feed: IFeed | undefined;
  formState: IDetailsTabFormState;
  onSuccess: () => void;
}

export interface IAddPermissionPayload {
  feed: IFeed;
  permissionToAdd: FeedPermission;
  permissions: FeedPermission[];
  onSuccess: () => void;
}

export interface IDeletePermissionPayload {
  feed: IFeed;
  permissionToDelete: FeedPermission;
  permissions: FeedPermission[];
  onSuccess: () => void;
}

export interface IUpdatePermissionsCompletedPayload {
  permissions: FeedPermission[];
}

export const FeedSettingsActions = {
  initLoad: (feed: IFeed) =>
    createAction<FeedSettingsActionTypes, ISettingsInitLoadPayload>(
      FeedSettingsActionTypes.InitLoad,
      { feed },
    ),
  initLoadStarted: () => createAction(FeedSettingsActionTypes.InitLoadStarted),
  initLoadCompleted: (settings: IFeedSettings) =>
    createAction<FeedSettingsActionTypes, ISettingsInitLoadCompletedPayload>(
      FeedSettingsActionTypes.InitLoadCompleted,
      { settings },
    ),
  initLoadFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.InitLoadFailed,
      { error },
    ),
  deleteFeed: (feed: IFeed, onSuccess: () => void) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IDeleteFeedPayload>(
        FeedSettingsActionTypes.DeleteFeed,
        {
          feed,
          onSuccess,
        },
      ),
    ),
  deleteFeedCompleted: () =>
    trackedAction(createAction(FeedSettingsActionTypes.DeleteFeedCompleted)),
  deleteFeedFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.DeleteFeedFailed,
        { error },
      ),
    ),
  permanentDeleteFeed: (feed: IFeed, onSuccess: () => void) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IDeleteFeedPayload>(
        FeedSettingsActionTypes.PermanentDeleteFeed,
        { feed, onSuccess },
      ),
    ),
  permanentDeleteFeedCompleted: () =>
    trackedAction(createAction(FeedSettingsActionTypes.PermanentDeleteFeedCompleted)),
  permanentDeleteFeedFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.PermanentDeleteFeedFailed,
        { error },
      ),
    ),
  restoreFeed: (feed: IFeed, onSuccess: () => void) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IRestoreFeedPayload>(
        FeedSettingsActionTypes.RestoreFeed,
        { feed, onSuccess },
      ),
    ),
  restoreFeedCompleted: () =>
    trackedAction(createAction(FeedSettingsActionTypes.RestoreFeedCompleted)),
  restoreFeedFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.RestoreFeedFailed,
        { error },
      ),
    ),
  saveGeneralFeedSettings: (
    feed: IFeed | undefined,
    formState: IDetailsTabFormState,
    onSuccess: () => void,
  ) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, ISaveGeneralFeedSettingsPayload>(
        FeedSettingsActionTypes.SaveGeneralFeedSettings,
        { feed, formState, onSuccess },
      ),
    ),
  saveGeneralFeedSettingsCompleted: () =>
    trackedAction(createAction(FeedSettingsActionTypes.SaveGeneralFeedSettingsCompleted)),
  saveGeneralFeedSettingsFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.SaveGeneralFeedSettingsFailed,
        { error },
      ),
    ),
  loadPermissions: (feed: IFeed) =>
    createAction<FeedSettingsActionTypes, ISettingsInitLoadPayload>(
      FeedSettingsActionTypes.LoadPermissions,
      { feed },
    ),
  loadPermissionsCompleted: (permissions: FeedPermission[]) =>
    createAction<FeedSettingsActionTypes, ILoadPermissionsCompletedPayload>(
      FeedSettingsActionTypes.LoadPermissionsCompleted,
      { permissions },
    ),
  loadPermissionsFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.LoadPermissionsFailed,
      { error },
    ),
  addPermission: (
    feed: IFeed,
    permissionToAdd: FeedPermission,
    permissions: FeedPermission[],
    onSuccess: () => void,
  ) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IAddPermissionPayload>(
        FeedSettingsActionTypes.AddPermission,
        { feed, permissionToAdd, permissions, onSuccess },
      ),
    ),
  addPermissionCompleted: (permissions: FeedPermission[]) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IUpdatePermissionsCompletedPayload>(
        FeedSettingsActionTypes.AddPermissionCompleted,
        { permissions: permissions },
      ),
    ),
  addPermissionFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.AddPermissionFailed,
        { error },
      ),
    ),
  deletePermission: (
    feed: IFeed,
    permissionToDelete: FeedPermission,
    permissions: FeedPermission[],
    onSuccess: () => void,
  ) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IDeletePermissionPayload>(
        FeedSettingsActionTypes.DeletePermission,
        { feed, permissionToDelete, permissions, onSuccess },
      ),
    ),
  deletePermissionCompleted: (permissions: FeedPermission[]) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IUpdatePermissionsCompletedPayload>(
        FeedSettingsActionTypes.DeletePermissionCompleted,
        { permissions: permissions },
      ),
    ),
  deletePermissionFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.DeletePermissionFailed,
        { error },
      ),
    ),
  loadViewsPermissions: (feed: IFeed, views: ViewIdentifier[]) =>
    createAction<FeedSettingsActionTypes, ILoadViewsPermissionsPayload>(
      FeedSettingsActionTypes.LoadViewsPermissions,
      { feed, views },
    ),
  loadViewsPermissionsCompleted: (permissionMap: ViewPermissionMap) =>
    createAction<FeedSettingsActionTypes, ILoadViewsPermissionsCompletedPayload>(
      FeedSettingsActionTypes.LoadViewsPermissionsCompleted,
      { permissionMap },
    ),
  loadViewsPermissionsFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.LoadViewsPermissionsFailed,
      { error },
    ),
  addOrEditView: (
    feed: IFeed,
    view: FeedView,
    views: FeedView[],
    isEdit: boolean,
    isDefaultEnabled: boolean,
    currentViewPermissions: FeedPermission[],
    feedPermsToSet: FeedPermission[],
    onSuccess: () => void,
  ) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IAddOrEditViewPayload>(
        FeedSettingsActionTypes.AddOrEditView,
        {
          feed,
          view,
          isEdit,
          currentViewPermissions,
          isDefaultEnabled,
          feedPermsToSet,
          onSuccess,
        },
      ),
    ),
  addOrEditViewCompleted: (
    newModifiedView: FeedView,
    permissions: FeedPermission[],
    defaultFeedViewId?: string,
  ) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IAddOrEditViewCompletedPayload>(
        FeedSettingsActionTypes.AddOrEditViewCompleted,
        { newModifiedView, permissions, defaultFeedViewId },
      ),
    ),
  addOrEditViewFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
        FeedSettingsActionTypes.AddOrEditViewFailed,
        { error },
      ),
    ),
  updateUpstreamList: (upstreams: IUpstreamSource[]) =>
    createAction<FeedSettingsActionTypes, IUpstreamsPayload>(
      FeedSettingsActionTypes.UpdateUpstreamList,
      { upstreams },
    ),
  saveUpstreamConfiguration: (feed: IFeed, upstreams: IUpstreamSource[], onSuccess: () => void) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, ISaveUpstreamsPayload>(
        FeedSettingsActionTypes.SaveUpstreamConfiguration,
        { feed, upstreams, onSuccess },
      ),
    ),
  saveUpstreamConfigurationCompleted: (upstreams: IUpstreamSource[]) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IUpstreamsPayload>(
        FeedSettingsActionTypes.SaveUpstreamConfigurationCompleted,
        { upstreams },
      ),
    ),
  saveUpstreamConfigurationFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
        FeedSettingsActionTypes.SaveUpstreamConfigurationFailed,
        { error },
      ),
    ),
  saveUpstreamConfigurationComponentReset: () =>
    trackedAction(
      createAction<FeedSettingsActionTypes, undefined>(
        FeedSettingsActionTypes.SaveUpstreamConfigurationComponentReset,
        undefined,
      ),
    ),
  loadFeeds: () => createAction(FeedSettingsActionTypes.LoadFeeds),
  loadFeedsCompleted: (feeds: Feed[]) =>
    createAction<FeedSettingsActionTypes, ILoadUpstreamFeedsCompleted>(
      FeedSettingsActionTypes.LoadFeedsCompleted,
      { feeds },
    ),
  loadFeedsFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.LoadFeedsFailed,
      { error },
    ),
  loadFeedViews: (feed: Feed) =>
    createAction<FeedSettingsActionTypes, ILoadFeedViewsPayload>(
      FeedSettingsActionTypes.LoadFeedViews,
      { feed },
    ),
  loadFeedViewsCompleted: (views: FeedView[]) =>
    createAction<FeedSettingsActionTypes, ILoadFeedViewsCompletedPayload>(
      FeedSettingsActionTypes.LoadFeedViewsCompleted,
      {
        views,
      },
    ),
  loadFeedViewsFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.LoadFeedViewsFailed,
      { error },
    ),
  loadServiceConnections: () => createAction(FeedSettingsActionTypes.LoadServiceConnections),
  loadServiceConnectionsCompleted: (serviceConnections: ServiceEndpoint[]) =>
    createAction<FeedSettingsActionTypes, ILoadServiceConnectionsCompleted>(
      FeedSettingsActionTypes.LoadServiceConnectionsCompleted,
      { serviceConnections },
    ),
  loadServiceConnectionsFailed: (error: string) =>
    createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
      FeedSettingsActionTypes.LoadServiceConnectionsFailed,
      { error },
    ),
  addUpstreams: (upstreams: IUpstreamSource[]) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IAddUpstreamsPayload>(
        FeedSettingsActionTypes.AddUpstreams,
        {
          upstreams,
        },
      ),
    ),
  makeViewDefault: (feed: IFeed, view: FeedView) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IMakeViewDefaultPayload>(
        FeedSettingsActionTypes.MakeViewDefault,
        {
          feed,
          view,
        },
      ),
    ),
  makeViewDefaultCompleted: (defaultViewId: string) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IMakeViewDefaultCompletedPayload>(
        FeedSettingsActionTypes.MakeViewDefaultCompleted,
        {
          defaultViewId,
        },
      ),
    ),
  makeViewDefaultFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ILoadFailedPayload>(
        FeedSettingsActionTypes.MakeViewDefaultFailed,
        { error },
      ),
    ),
  deleteView: (feed: IFeed, view: FeedView, onSuccess: () => void) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IDeleteViewPayload>(
        FeedSettingsActionTypes.DeleteView,
        {
          feed,
          view,
          onSuccess,
        },
      ),
    ),
  deleteViewCompleted: (feed: IFeed, view: FeedView) =>
    trackedAction(
      createAction<FeedSettingsActionTypes, IDeleteViewCompletedPayload>(
        FeedSettingsActionTypes.DeleteViewCompleted,
        {
          feed,
          view,
        },
      ),
    ),
  deleteViewFailed: (error: string) =>
    trackedErrorAction(
      createAction<FeedSettingsActionTypes, ISaveFailedPayload>(
        FeedSettingsActionTypes.DeleteViewFailed,
        { error },
      ),
    ),
};

export type FeedSettingsActions = ActionsUnion<typeof FeedSettingsActions>;
